import { db } from '@/services/database'
import { isManboAudioUrl, manboClipKey, MANBO_CACHE_NAME, MANBO_FAILURE_SETTING, MANBO_LOCK, type ManboClip } from './manbo'
import { noticeText } from './speech-text'

const UNUSED_MAX_AGE_MS = 3 * 24 * 60 * 60 * 1000

export type ManboPreparationStage = 'idle' | 'waiting' | 'generating' | 'downloading' | 'validating'
export type ManboCacheStatus = 'ready' | 'missing' | 'failed' | 'unavailable'
export interface ManboPreparationState { preparing: boolean; ready: number; total: number; error: string; stage: ManboPreparationStage; currentText: string }

function environmentError(): string {
  if (!globalThis.isSecureContext) return 'manbo-insecure'
  if (typeof caches === 'undefined' || typeof Audio !== 'function' || !navigator.serviceWorker || !navigator.locks?.request) return 'manbo-capabilities'
  return navigator.serviceWorker.controller ? '' : 'manbo-worker-pending'
}

export function manboCacheSupported(): boolean {
  return !environmentError()
}

/** Audio bodies stay opaque: the worker serves them to a no-cors media element. */
export class ManboAudio {
  private desired: string[] = []
  private checked = new Set<string>()
  private ready = new Set<string>()
  private listeners = new Set<() => void>()
  private running?: Promise<void>
  private controller?: AbortController
  private cancelWorkerWait?: () => void
  private disposed = false
  private preparing = false
  private error = ''
  private stage: ManboPreparationStage = 'idle'
  private currentText = ''

  get state(): ManboPreparationState {
    return { preparing: this.preparing, ready: this.desired.filter(text => this.ready.has(text)).length, total: this.desired.length, error: this.error, stage: this.stage, currentText: this.currentText }
  }
  subscribe(listener: () => void): () => void { this.listeners.add(listener); return () => this.listeners.delete(listener) }
  private notify(): void { this.listeners.forEach(listener => listener()) }
  private phase(stage: ManboPreparationStage, text = ''): void { this.stage = stage; this.currentText = text; this.notify() }

  prepare(texts: string[]): Promise<void> { return this.start(texts, false) }

  private start(texts: string[], resetFailure: boolean): Promise<void> {
    this.desired = [...new Set(texts.map(text => text.trim()).filter(Boolean))]
    this.checked.clear()
    if (this.disposed || !this.desired.length && !resetFailure) { this.cancelWorkerWait?.(); this.notify(); return this.running ?? Promise.resolve() }
    if (this.running) { this.notify(); return this.running }
    this.running = this.drain(resetFailure).finally(() => { this.running = undefined; this.preparing = false; this.phase('idle') })
    return this.running
  }

  async retry(texts = this.desired): Promise<void> {
    this.desired = [...new Set(texts.map(text => text.trim()).filter(Boolean))]
    if (this.running) await this.running
    return this.start(this.desired, true)
  }

  async wait(signal: AbortSignal): Promise<void> {
    if (!this.running || signal.aborted) return
    await new Promise<void>(resolve => {
      const done = () => { signal.removeEventListener('abort', done); resolve() }
      signal.addEventListener('abort', done, { once: true })
      void this.running!.then(done, done)
    })
  }

  async cachedUrl(text: string): Promise<string | null> {
    if (!manboCacheSupported() || !text.trim()) return null
    const cache = await caches.open(MANBO_CACHE_NAME)
    const clip = await db.manboClips.get(manboClipKey(text))
    if (!clip || clip.state !== 'ready' || !isManboAudioUrl(clip.url)) return null
    if (!await cache.match(clip.url)) {
      this.ready.delete(text); this.error = 'manbo-cache-missing'; this.notify(); return null
    }
    return clip.url
  }

  markUsed(text: string): void {
    // Updating an existing record never recreates a clip removed during playback.
    void db.manboClips.update(manboClipKey(text), { usedAt: Date.now() }).catch(() => {})
  }

  private async referencedKeys(): Promise<Set<string>> {
    const items = await db.buffs.toArray()
    return new Set(items.map(item => manboClipKey(noticeText(item.name, 'zh-CN'))))
  }

  async cleanupExpired(): Promise<void> {
    const cutoff = Date.now() - UNUSED_MAX_AGE_MS
    if (typeof caches === 'undefined' || !navigator.locks?.request) return
    await navigator.locks.request(MANBO_LOCK, async () => {
      if (this.disposed) return
      const referenced = await this.referencedKeys(), clips = await db.manboClips.toArray()
      const expired = clips.filter(clip => !referenced.has(clip.key) && clip.usedAt < cutoff)
      if (!expired.length) return
      const removed = new Set(expired.map(clip => clip.key))
      const retainedUrls = new Set(clips.filter(clip => !removed.has(clip.key)).map(clip => clip.url))
      const cache = await caches.open(MANBO_CACHE_NAME), deletedUrls = new Set<string>()
      for (const clip of expired) {
        if (isManboAudioUrl(clip.url) && !retainedUrls.has(clip.url) && !deletedUrls.has(clip.url)) {
          await cache.delete(clip.url)
          deletedUrls.add(clip.url)
        }
        await db.manboClips.delete(clip.key)
        this.ready.delete(clip.text); this.notify()
      }
    })
  }

  async cacheStatus(text: string): Promise<ManboCacheStatus> {
    try {
      if (typeof caches === 'undefined') return 'unavailable'
      const clip = await db.manboClips.get(manboClipKey(text))
      if (clip?.state === 'failed') return 'failed'
      if (clip?.state !== 'ready' || !isManboAudioUrl(clip.url)) return 'missing'
      const cache = await caches.open(MANBO_CACHE_NAME)
      return await cache.match(clip.url) ? 'ready' : 'missing'
    } catch { return 'unavailable' }
  }

  private async drain(resetFailure: boolean): Promise<void> {
    this.preparing = true; this.error = ''; this.notify()
    try {
      await this.waitForWorker()
      if (this.disposed) return
      if (resetFailure) await navigator.locks.request(MANBO_LOCK, () => db.settings.delete(MANBO_FAILURE_SETTING))
      while (!this.disposed) {
        const text = this.desired.find(text => !this.checked.has(text))
        if (!text) return
        await navigator.locks.request(MANBO_LOCK, async () => {
          if (this.disposed || !this.desired.includes(text)) return
          await this.prepareClip(text)
        })
        this.checked.add(text)
        this.phase('idle')
      }
    } catch (error) {
      if (this.disposed) return
      this.error = error instanceof Error && error.message.startsWith('manbo-') ? error.message : 'manbo-storage'
      this.notify()
    }
  }

  private async waitForWorker(): Promise<void> {
    const error = environmentError()
    if (!error) return
    if (error !== 'manbo-worker-pending') throw new Error(error)
    this.phase('waiting')
    await new Promise<void>((resolve, reject) => {
      const workers = navigator.serviceWorker
      const finish = (error?: string) => {
        clearTimeout(timeout); workers.removeEventListener('controllerchange', changed)
        this.cancelWorkerWait = undefined
        if (error) reject(new Error(error)); else resolve()
      }
      const changed = () => { if (workers.controller) finish() }
      const timeout = setTimeout(() => finish('manbo-worker-timeout'), 5000)
      this.cancelWorkerWait = () => finish()
      workers.addEventListener('controllerchange', changed)
      changed()
    })
  }

  private async prepareClip(text: string): Promise<void> {
    // Another tab may still have this deleted or renamed BUFF in its queue.
    if (!(await this.referencedKeys()).has(manboClipKey(text))) { this.ready.delete(text); return }
    const key = manboClipKey(text), cache = await caches.open(MANBO_CACHE_NAME)
    let clip: ManboClip = await db.manboClips.get(key) ?? { key, text, state: 'pending', usedAt: Date.now() }
    if (clip.state === 'ready' && isManboAudioUrl(clip.url) && await cache.match(clip.url)) {
      this.ready.add(text); this.notify(); return
    }
    this.ready.delete(text)
    const failure = await db.settings.get(MANBO_FAILURE_SETTING)
    if (failure && !clip.url) throw new Error(String(failure.value))
    this.controller = new AbortController()
    const signal = this.controller.signal
    const timeout = setTimeout(() => this.controller?.abort(), 60_000)
    try {
      if (!isManboAudioUrl(clip.url)) {
        this.phase('generating', text)
        const url = new URL('https://api.milorapart.top/apis/mbAIsc')
        url.search = new URLSearchParams({ text }).toString()
        const response = await fetch(url, { credentials: 'omit', signal })
        let data: { code?: unknown; msg?: unknown; url?: unknown } | null = null
        try { data = await response.json() } catch { /* HTTP and shape checks report the failure below. */ }
        if (!response.ok || data?.code !== 200) {
          if (response.ok && data?.code == null) throw new Error('manbo-response')
          const detail = typeof data?.msg === 'string' ? data.msg.slice(0, 200) : !response.ok ? `HTTP ${response.status}` : `code ${String(data?.code).slice(0, 40)}`
          throw new Error(`manbo-api:${detail}`)
        }
        if (!isManboAudioUrl(data.url)) throw new Error('manbo-response')
        clip = { ...clip, url: data.url, state: 'pending', error: undefined }
        // Persist the generated URL before downloading, so retries can reuse it.
        await db.manboClips.put(clip)
      }
      if (!await cache.match(clip.url!)) {
        this.phase('downloading', text)
        const response = await fetch(clip.url!, { mode: 'no-cors', credentials: 'omit', cache: 'no-store', signal })
        if (response.type !== 'opaque' && !response.ok) throw new Error('manbo-download')
        await cache.put(clip.url!, response)
      }
      this.phase('validating', text)
      try { await validateAudio(clip.url!, signal) }
      catch (error) { await cache.delete(clip.url!); throw error }
      await db.manboClips.put({ ...clip, state: 'ready', error: undefined, usedAt: Date.now() })
      this.ready.add(text); this.error = ''; this.notify()
    } catch (caught) {
      if (this.disposed) return
      const error = caught instanceof Error && caught.message.startsWith('manbo-') ? caught.message
        : caught instanceof DOMException && ['QuotaExceededError', 'SecurityError'].includes(caught.name) ? 'manbo-storage'
          : clip.url ? 'manbo-download' : 'manbo-api'
      await db.manboClips.put({ ...clip, url: clip.state === 'failed' ? undefined : clip.url, state: 'failed', error })
      await db.settings.put({ key: MANBO_FAILURE_SETTING, value: error, updatedAt: Date.now() })
      throw new Error(error)
    } finally { clearTimeout(timeout); this.controller = undefined }
  }

  dispose(): void { this.disposed = true; this.desired = []; this.cancelWorkerWait?.(); this.controller?.abort(); this.listeners.clear() }
}

function validateAudio(url: string, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const audio = new Audio()
    const finish = (error?: string) => {
      clearTimeout(timeout); signal.removeEventListener('abort', abort)
      audio.onloadedmetadata = null; audio.onerror = null
      audio.removeAttribute('src'); audio.load()
      if (error) reject(new Error(error)); else resolve()
    }
    const abort = () => finish('manbo-download')
    const timeout = setTimeout(() => finish('manbo-audio'), 15_000)
    audio.preload = 'metadata'
    audio.onloadedmetadata = () => finish(Number.isFinite(audio.duration) && audio.duration > 0 ? undefined : 'manbo-audio')
    audio.onerror = () => finish('manbo-audio')
    signal.addEventListener('abort', abort, { once: true })
    if (signal.aborted) abort()
    else { audio.src = url; audio.load() }
  })
}
